import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { formatarMoeda } from '../services/formatters';
import { temPermissao } from '../utils/permissoes';

function data(v) {
  if (!v) return '—';
  const s = typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : String(v);
  const [y, m, d] = s.split('T')[0].split('-');
  if (!y || !m || !d) return '—';
  return `${d}/${m}/${y}`;
}

function diasLabel(d) {
  const n = parseInt(d);
  if (n === 0) return 'Hoje';
  if (n === 1) return 'Amanhã';
  return `Em ${n} dia${n > 1 ? 's' : ''}`;
}

function KpiCard({ icone, titulo, valor, sub, cor, destaque }) {
  return (
    <div style={{
      background: destaque ? cor : 'var(--bg-card)',
      border: `1px solid ${destaque ? cor : 'var(--border-color)'}`,
      borderLeft: `4px solid ${cor}`,
      borderRadius: 12, padding: '18px 22px', flex: 1, minWidth: 140,
    }}>
      <div style={{ fontSize: 24, marginBottom: 6 }}>{icone}</div>
      <div style={{ fontSize: 12, color: destaque ? '#fff' : 'var(--text-muted)', marginBottom: 4 }}>{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: destaque ? '#fff' : 'var(--text-primary)' }}>{valor}</div>
      {sub && <div style={{ fontSize: 11, color: destaque ? 'rgba(255,255,255,0.75)' : 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function BadgeAlerta({ tipo }) {
  const map = {
    prazo_obra: { label: 'Prazo da obra', cor: '#ef4444', bg: '#fef2f2' },
    pagamento_vencido: { label: 'Pagamento vencido', cor: '#f59e0b', bg: '#fffbeb' },
    pausada: { label: 'Obra pausada', cor: '#6b7280', bg: '#f9fafb' },
    prazo_obra_vence: { label: 'Prazo a vencer', cor: '#3b82f6', bg: '#eff6ff' },
    pagamento: { label: 'Pagamento', cor: '#8b5cf6', bg: '#f5f3ff' },
  };
  const s = map[tipo] || { label: tipo, cor: '#6b7280', bg: '#f9fafb' };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
      color: s.cor, background: s.bg, border: `1px solid ${s.cor}22`,
    }}>{s.label}</span>
  );
}

const PLANO_COR = { bronze: '#b45309', prata: '#6b7280', ouro: '#d97706', diamante: '#7c3aed' };
const PLANO_BG  = { bronze: '#fef3c7', prata: '#f3f4f6', ouro: '#fffbeb', diamante: '#f5f3ff' };

function barraLimite(uso, limite) {
  if (limite === null) return null;
  const pct = Math.min((uso / limite) * 100, 100);
  const cor = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#22c55e';
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ height: 4, background: 'var(--border-color)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: cor, borderRadius: 4, transition: 'width .3s' }} />
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{uso} / {limite}</div>
    </div>
  );
}

function Dashboard({ navegarPara }) {
  const [resumo, setResumo] = useState(null);
  const [atencao, setAtencao] = useState(null);
  const [proximos, setProximos] = useState([]);
  const [plano, setPlano] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get('/home/resumo'),
        api.get('/home/atencao'),
        api.get('/home/proximos-vencimentos'),
        api.get('/tenant/status'),
      ]);
      setResumo(r1.data);
      setAtencao(r2.data);
      setProximos(r3.data);
      setPlano(r4.data);
    } catch { }
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const totalAtencao = atencao
    ? atencao.obras_atrasadas.length + atencao.servicos_atrasados.length + atencao.pagamentos_vencidos.length + atencao.pausadas.length
    : 0;

  if (carregando) return <div style={{ padding: 40, color: '#6b7280' }}>Carregando...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>🏠 Início</h2>
        <button className="btn-secondary" style={{ fontSize: 12 }} onClick={carregar}>↺ Atualizar</button>
      </div>

      {/* Card do plano */}
      {plano && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
          background: 'var(--bg-card)',
          border: `1px solid ${PLANO_COR[plano.plano] || '#7c3aed'}55`,
          borderLeft: `4px solid ${PLANO_COR[plano.plano] || '#7c3aed'}`,
          borderRadius: 10, padding: '12px 18px', marginBottom: 20,
        }}>
          <div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Seu plano</span>
            <div style={{ fontSize: 16, fontWeight: 700, color: PLANO_COR[plano.plano] || '#7c3aed', textTransform: 'capitalize' }}>
              {plano.plano}
            </div>
          </div>
          <div style={{ width: 1, height: 36, background: 'var(--border-color)' }} />
          <div style={{ minWidth: 80 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Obras ativas</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {plano.limites.obras.uso}{plano.limites.obras.limite ? ` / ${plano.limites.obras.limite}` : ' (ilimitado)'}
            </div>
            {barraLimite(plano.limites.obras.uso, plano.limites.obras.limite)}
          </div>
          <div style={{ minWidth: 80 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Usuários</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              {plano.limites.usuarios.uso}{plano.limites.usuarios.limite ? ` / ${plano.limites.usuarios.limite}` : ' (ilimitado)'}
            </div>
            {barraLimite(plano.limites.usuarios.uso, plano.limites.usuarios.limite)}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <a href="https://cronoobra.com.br" target="_blank" rel="noreferrer"
              style={{ fontSize: 12, color: PLANO_COR[plano.plano] || '#7c3aed', textDecoration: 'none', fontWeight: 600 }}>
              Ver planos →
            </a>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
        <KpiCard
          icone="🏗️" titulo="Obras em Andamento"
          valor={resumo?.em_andamento ?? '—'}
          cor="#3b82f6"
        />
        <KpiCard
          icone="⚠️" titulo="Obras Atrasadas"
          valor={resumo?.atrasadas ?? '—'}
          sub="Prazo combinado vencido"
          cor="#ef4444"
          destaque={resumo?.atrasadas > 0}
        />
        <KpiCard
          icone="📅" titulo="Vencem em 7 dias"
          valor={resumo?.a_vencer_7dias ?? '—'}
          sub="Prazo combinado"
          cor="#f59e0b"
          destaque={resumo?.a_vencer_7dias > 0}
        />
        {temPermissao('financeiro_ver') && (
          <KpiCard
            icone="💰" titulo="A Receber"
            valor={formatarMoeda(resumo?.a_receber ?? 0)}
            sub="Saldo pendente total"
            cor="#22c55e"
          />
        )}
      </div>

      {/* Requer atenção */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>⚠️ Requer Atenção</h3>
          {totalAtencao > 0 && (
            <span style={{
              background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700,
              padding: '2px 8px', borderRadius: 20,
            }}>{totalAtencao}</span>
          )}
        </div>

        {totalAtencao === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#22c55e' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
            <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Tudo em dia!</p>
            <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>Nenhuma obra ou pagamento requer atenção imediata.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

            {atencao?.obras_atrasadas.map(o => (
              <div key={`obra-${o.obraid}`} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 16px', background: 'var(--bg-card)',
                border: '1px solid #fecaca', borderRadius: 10,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: 14, color: 'var(--text-primary)' }}>{o.nome_obra}</strong>
                    <BadgeAlerta tipo="prazo_obra" />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {o.nome_cliente} · Prazo era {data(o.prazo_combinado)}
                    <span style={{ color: '#ef4444', fontWeight: 600 }}> · {o.dias_atraso} dia{o.dias_atraso > 1 ? 's' : ''} em atraso</span>
                  </div>
                </div>
                <button className="btn-secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                  onClick={() => navegarPara && navegarPara('obras', o.obraid, 'dashboard')}>
                  Ver obra →
                </button>
              </div>
            ))}

            {atencao?.servicos_atrasados.map(o => (
              <div key={`svc-${o.obraid}`} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 16px', background: 'var(--bg-card)',
                border: '1px solid #fecaca', borderRadius: 10,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: 14, color: 'var(--text-primary)' }}>{o.nome_obra}</strong>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, color: '#ef4444', background: 'transparent', border: '1px solid #ef444444' }}>Serviço atrasado</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {o.nome_cliente} · Serviço: <strong>{o.nome_servico}</strong> · Era pra entregar {data(o.data_fim)}
                    <span style={{ color: '#ef4444', fontWeight: 600 }}> · {o.dias_atraso} dia{o.dias_atraso > 1 ? 's' : ''} em atraso</span>
                  </div>
                </div>
                <button className="btn-secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                  onClick={() => navegarPara && navegarPara('obras', o.obraid, 'dashboard')}>
                  Ver obra →
                </button>
              </div>
            ))}

            {atencao?.pagamentos_vencidos.map((p, i) => (
              <div key={`pgto-${i}`} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 16px', background: 'var(--bg-card)',
                border: '1px solid #fde68a', borderRadius: 10,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: 14, color: 'var(--text-primary)' }}>{p.nome_obra}</strong>
                    <BadgeAlerta tipo="pagamento_vencido" />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {p.nome_cliente} · {p.forma_pagamento} · Prazo era {data(p.prazo_pagamento)}
                    <span style={{ color: '#f59e0b', fontWeight: 600 }}> · {p.dias_atraso} dia{p.dias_atraso > 1 ? 's' : ''} em atraso</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}> · Pendente: {formatarMoeda(p.valor_pendente)}</span>
                  </div>
                </div>
                <button className="btn-secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                  onClick={() => navegarPara && navegarPara('obras', p.obraid)}>
                  Ver obra →
                </button>
              </div>
            ))}

            {atencao?.pausadas.map(o => (
              <div key={`pausada-${o.obraid}`} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 16px', background: 'var(--bg-card)',
                border: '1px solid var(--border-color)', borderRadius: 10,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: 14, color: 'var(--text-primary)' }}>{o.nome_obra}</strong>
                    <BadgeAlerta tipo="pausada" />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {o.nome_cliente}{o.prazo_combinado ? ` · Prazo: ${data(o.prazo_combinado)}` : ''}
                  </div>
                </div>
                <button className="btn-secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                  onClick={() => navegarPara && navegarPara('obras', o.obraid, 'dashboard')}>
                  Ver obra →
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Próximos vencimentos */}
      <div className="card">
        <h3 style={{ marginBottom: 16 }}>📅 Próximos Vencimentos <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 400 }}>(15 dias)</span></h3>

        {proximos.length === 0 ? (
          <p style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: 13 }}>
            Nenhum vencimento nos próximos 15 dias.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {proximos.map((item, i) => {
              const isPgto = item.tipo === 'pagamento';
              const diasR = parseInt(item.dias_restantes);
              const urgente = diasR <= 3;
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '12px 16px',
                  background: 'var(--bg-card)',
                  border: `1px solid ${urgente ? '#fed7aa' : 'var(--border-color)'}`,
                  borderLeft: `3px solid ${urgente ? '#f97316' : '#3b82f6'}`,
                  borderRadius: 10,
                }}>
                  <div style={{
                    minWidth: 72, textAlign: 'center',
                    background: urgente ? '#f97316' : '#3b82f6',
                    color: '#fff', borderRadius: 8, padding: '6px 4px',
                  }}>
                    <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{diasR}</div>
                    <div style={{ fontSize: 10, marginTop: 2 }}>dia{diasR !== 1 ? 's' : ''}</div>
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <strong style={{ fontSize: 14, color: 'var(--text-primary)' }}>{item.nome_obra}</strong>
                      <BadgeAlerta tipo={isPgto ? 'pagamento' : 'prazo_obra_vence'} />
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {item.nome_cliente}
                      {isPgto
                        ? ` · ${item.forma_pagamento} · ${formatarMoeda(item.valor_pendente)} pendente · Vence ${data(item.prazo_pagamento)}`
                        : ` · Prazo combinado: ${data(item.prazo_combinado)}`
                      }
                    </div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: urgente ? '#f97316' : '#3b82f6', marginBottom: 4 }}>
                      {diasLabel(diasR)}
                    </div>
                    <button className="btn-secondary" style={{ fontSize: 12 }}
                      onClick={() => navegarPara && navegarPara('obras', item.obraid, 'dashboard')}>
                      Ver →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
